import { type LucideIcon } from "lucide-react";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Link } from "react-router";
import { useCurrentPath } from "@/hooks/useCurrentPath";
import { useFullDashboardPath } from "@/hooks/useFullDashboardPath";
import { cn } from "@/util/cn";
import { LinkItem } from "./layouts/DashboardLayout";
import { NavMenu } from "./NavMenu";

type NavItemsProps = {
  title?: string;
  items: LinkItem[];
};

export function NavItems({ title, items }: NavItemsProps) {
  const currentPath = useCurrentPath();
  const fullDashboardPath = useFullDashboardPath();
  const { setOpenMobile } = useSidebar();

  const isActive = (url: string) => {
    if (fullDashboardPath === "") {
      return url === "";
    }
    return fullDashboardPath === url;
  };

  const handleClick = () => {
    setOpenMobile(false);
  };

  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      {title && (
        <SidebarGroupLabel className="text-lg font-semibold tracking-wide uppercase text-sidebar-foreground/80">
          {title}
        </SidebarGroupLabel>
      )}
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const active = isActive(item.url);
            
            // If item has a menu, render NavMenu component
            if (item.menu && item.menu.length > 0) {
              return <NavMenu key={item.name} item={item} />;
            }
            
            // Otherwise render regular menu item
            return (
              <SidebarMenuItem key={item.name}>
                <Link
                  to={`/dashboard/${item.url}`}
                  className="flex items-center text-lg h-full w-full"
                  onClick={handleClick}
                >
                  <SidebarMenuButton
                    className="text-base"
                    isActive={active}
                    size={"lg"}
                    asChild
                  >
                    <item.icon className="mr-2" />
                    <span className="leading-12 flex-1">{item.name}</span>
                    {item.badge && (
                      <SidebarMenuBadge
                        className={cn(
                          "bg-white text-black font-semibold border",
                          item.badgeVariant === "outline" &&
                            "bg-brand-neutral-700/50 text-white/80",
                          active &&
                            item.badgeVariant === "outline" &&
                            "text-white"
                        )}
                      >
                        {item.badge}
                      </SidebarMenuBadge>
                    )}
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
